/**
 * „Ausschreibung hinzufügen" — a tender no search profile found: sent by e-mail, published on a portal
 * we do not crawl, or older than the profile. Tender documents, a free text, or both.
 *
 * The server does the reading (tri-server `POST /:customer/tenders/manual`, docs/tender-from-documents.md):
 * it extracts title, buyer, place and deadline and files the tender under Triage. Text alone is answered
 * within the request. With files it answers "processing" and finishes by itself, so closing this dialog
 * while a large PDF is being read loses nothing — the polling here only shows when it is done.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { AlertTriangle, FileText, Loader2, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { contextUploadAccept, formatMegabytes, sortUploadCandidates } from "../../components/contextFileTypes";
import { FileTooLargeError } from "../../general/Errors";
import { authenticationAtom } from "../../State";
import { userAPI } from "../../UserAPI";
import { ManualTenderCreated, useTenderSearchProfileAPI } from "../../TenderSearchProfileAPI";

/** Consecutive failed status checks after which the dialog stops asking — the server does not stop. */
const MAX_FAILED_STATUS_CHECKS = 20;

/** A one-page PDF is not "0 MB". */
const formatFileSize = (bytes: number): string =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : formatMegabytes(bytes);

type Phase = "edit" | "submitting" | "processing" | "lost";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (tender: ManualTenderCreated) => void;
}

export function AddTenderDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer: string = authentication?.customer;
  const api = useTenderSearchProfileAPI();

  const [files, setFiles] = useState<File[]>([]);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<Phase>("edit");
  const [error, setError] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [maxBytes, setMaxBytes] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Bumped whenever the dialog closes or a result arrives: a poll or an answer that belongs to an
  // earlier opening is dropped rather than applied to this one.
  const runRef = useRef(0);

  useEffect(() => {
    runRef.current++;
    if (!open) return;
    setFiles([]);
    setText("");
    setPhase("edit");
    setError(null);
    setProblem(null);
  }, [open]);

  useEffect(
    () => () => {
      runRef.current++;
    },
    []
  );

  // The same knob, from the same source, as the chat's pre-flight check.
  useEffect(() => {
    if (!open || !customer) return;
    let cancelled = false;
    userAPI
      .getCustomerSettings(customer)
      .then((settings: any) => {
        if (!cancelled && typeof settings?.limits?.maxUploadFileSizeBytes === "number") {
          setMaxBytes(settings.limits.maxUploadFileSizeBytes);
        }
      })
      .catch(() => {
        /* Without the limit nothing is refused here and the server's 413 decides. */
      });
    return () => {
      cancelled = true;
    };
  }, [open, customer]);

  const busy = phase === "submitting" || phase === "processing";
  const canSubmit = !busy && (files.length > 0 || text.trim().length > 0);

  const addFiles = (selected: File[]) => {
    const { valid, unsupported, tooLarge } = sortUploadCandidates(selected, maxBytes);
    const names = (list: File[]) => {
      const shown = list.slice(0, 3).map((file) => file.name);
      const remaining = list.length - shown.length;
      return remaining > 0 ? `${shown.join(", ")} (+${remaining})` : shown.join(", ");
    };
    const problems: string[] = [];
    if (unsupported.length > 0) {
      problems.push(
        t("chatExchange.unsupportedFileType", {
          files: names(unsupported),
          formats: t("chatExchange.supportedFormats"),
        })
      );
    }
    if (tooLarge.length > 0 && maxBytes) {
      problems.push(t("chatExchange.fileTooLarge", { files: names(tooLarge), maxSize: formatMegabytes(maxBytes) }));
    }
    setProblem(problems.length > 0 ? problems.join(" ") : null);
    // The same file picked twice is one file.
    setFiles((previous) => [
      ...previous,
      ...valid.filter(
        (file) => !previous.some((known) => known.name === file.name && known.size === file.size)
      ),
    ]);
  };

  const finish = (tender: ManualTenderCreated) => {
    runRef.current++;
    onCreated(tender);
  };

  const fail = (message: string) => {
    setPhase("edit");
    setError(message);
  };

  const watch = async (intakeId: string, run: number) => {
    let failedChecks = 0;
    for (let attempt = 0; runRef.current === run; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, Math.min(10000, 2000 + attempt * 500)));
      if (runRef.current !== run) return;
      try {
        const state = await api.getManualTenderIntake(intakeId);
        if (runRef.current !== run) return;
        failedChecks = 0;
        if (state.status === "created") return finish(state);
        if (state.status === "failed") return fail(state.error);
      } catch {
        // A missed check is not a failed tender. After enough of them, say that we lost sight of it.
        if (++failedChecks >= MAX_FAILED_STATUS_CHECKS) {
          setPhase("lost");
          return;
        }
      }
    }
  };

  const submit = async () => {
    const run = runRef.current;
    setPhase("submitting");
    setError(null);
    try {
      const answer = await api.createManualTender(files, text);
      if (runRef.current !== run) return;
      if (answer.status === "created") return finish(answer);
      if (answer.status === "failed") return fail(answer.error);
      if (!answer.intakeId) return setPhase("lost");
      setPhase("processing");
      watch(answer.intakeId, run);
    } catch (err: any) {
      if (runRef.current !== run) return;
      fail(
        err instanceof FileTooLargeError
          ? t("chatExchange.fileTooLargeShort", { maxSize: formatMegabytes(err.maxFileSizeBytes) })
          : err?.message ?? String(err)
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-2rem)] sm:max-w-[640px]"
        // A stray click beside the dialog must not throw away the progress view; the close button
        // still works, and says what happens then.
        onInteractOutside={(event) => {
          if (busy) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("tenderBoard.add.title")}</DialogTitle>
          <DialogDescription>{t("tenderBoard.add.description")}</DialogDescription>
        </DialogHeader>

        <div className="flex min-w-0 flex-col gap-1.5">
          <Label>{t("tenderBoard.add.files")}</Label>
          <div
            className={`flex flex-col items-center gap-2 rounded-md border border-dashed px-3 py-4 text-center text-sm ${
              dragging ? "border-ring bg-muted" : "border-input"
            }`}
            onDragOver={(event) => {
              event.preventDefault();
              if (!busy) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              if (!busy) addFiles(Array.from(event.dataTransfer.files ?? []));
            }}
          >
            <span className="text-muted-foreground">{t("tenderBoard.add.dropHint")}</span>
            <Button variant="outline" size="sm" className="gap-2" disabled={busy} onClick={() => inputRef.current?.click()}>
              <Upload aria-hidden className="h-3.5 w-3.5" />
              {t("tenderBoard.add.pick")}
            </Button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={contextUploadAccept}
              className="hidden"
              onChange={(event) => {
                const picked = Array.from(event.target.files ?? []);
                // Cleared, so picking the same file again still fires a change.
                event.target.value = "";
                if (picked.length > 0) addFiles(picked);
              }}
            />
          </div>

          {files.length > 0 && (
            <ul className="space-y-1">
              {files.map((file) => (
                <li key={`${file.name}-${file.size}`} className="flex items-center gap-2 text-sm">
                  <FileText aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
                  <button
                    type="button"
                    className="shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    title={t("global.remove")}
                    aria-label={t("global.remove")}
                    disabled={busy}
                    onClick={() => setFiles((previous) => previous.filter((known) => known !== file))}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {problem && <div className="text-xs text-amber-700">{problem}</div>}
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="add-tender-text">{t("tenderBoard.add.text")}</Label>
          <Textarea
            id="add-tender-text"
            rows={6}
            className="min-h-32"
            placeholder={t("tenderBoard.add.textPlaceholder")}
            value={text}
            disabled={busy}
            onChange={(event) => setText(event.target.value)}
          />
          <span className="text-[11px] text-muted-foreground">{t("tenderBoard.add.requirement")}</span>
        </div>

        {phase === "processing" && (
          <div className="flex items-start gap-2 rounded border border-border bg-muted px-3 py-2 text-sm">
            <Loader2 aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
            {t("tenderBoard.add.processing")}
          </div>
        )}
        {phase === "lost" && (
          <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {t("tenderBoard.add.statusUnknown")}
          </div>
        )}
        {error && (
          <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
            <AlertTriangle aria-hidden className="mr-1 inline h-3.5 w-3.5" />
            {error}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" className="max-sm:w-full" onClick={() => onOpenChange(false)}>
            {phase === "edit" ? t("global.cancel") : t("global.close")}
          </Button>
          {phase !== "lost" && (
            <Button className="gap-2 max-sm:w-full" disabled={!canSubmit} onClick={submit}>
              {busy && <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />}
              {busy ? t("tenderBoard.add.submitting") : t("tenderBoard.add.submit")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
